import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Badge,
  useDisclosure,
  Container,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { useAuth } from '../contexts/AuthContext';
import {
  getExcludedItems,
  createExcludedItem,
  updateExcludedItem,
  deleteExcludedItem,
} from '../services/api';

const ExcludedItemsManagement = () => {
  const [excludedItems, setExcludedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', remarks: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const cancelRef = React.useRef();
  const toast = useToast();
  const { currentUser } = useAuth();

  // 見積もり外項目一覧を取得
  const fetchExcludedItems = async () => {
    try {
      setLoading(true);
      const items = await getExcludedItems();
      setExcludedItems(items || []); // nullやundefinedの場合は空配列
      setError(null);
    } catch (err) {
      console.error('見積もり外項目の取得エラー:', err);
      setExcludedItems([]); // エラー時は空配列を設定
      setError(err.message);
      // 初回アクセス時のエラーはトーストを表示しない
      if (!err.message.includes('見積もり外項目の取得に失敗しました')) {
        toast({
          title: 'エラー',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExcludedItems();
  }, []);

  // モーダルを開く（新規作成または編集）
  const handleOpenModal = (item = null) => {
    if (item) {
      setIsEditing(true);
      setSelectedItem(item);
      setFormData({ name: item.name, remarks: item.remarks || '' });
    } else {
      setIsEditing(false);
      setSelectedItem(null);
      setFormData({ name: '', remarks: '' });
    }
    onOpen();
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setIsEditing(false);
    setSelectedItem(null);
    setFormData({ name: '', remarks: '' });
    onClose();
  };

  // 見積もり外項目を保存
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'エラー',
        description: '名称を入力してください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateExcludedItem(selectedItem.id, formData);
        toast({
          title: '成功',
          description: '見積もり外項目を更新しました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await createExcludedItem(formData);
        toast({
          title: '成功',
          description: '見積もり外項目を作成しました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
      fetchExcludedItems(); // 一覧を再取得
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 削除確認ダイアログを開く
  const handleOpenDeleteDialog = (item) => {
    setItemToDelete(item);
    onDeleteOpen();
  };

  // 見積もり外項目を削除
  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteExcludedItem(itemToDelete.id);
      toast({
        title: '成功',
        description: '見積もり外項目を削除しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setItemToDelete(null);
      fetchExcludedItems(); // 一覧を再取得
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="red.500" textAlign="center">
          {error}
        </Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Heading size="lg" color="gray.700">
            見積もり外項目管理
          </Heading>
          <Button
            leftIcon={<AddIcon />}
            bg="black"
            color="white"
            _hover={{ bg: 'gray.700' }}
            onClick={() => handleOpenModal()}
          >
            新規追加
          </Button>
        </Flex>

        <Box bg="gray.100" p={4} borderRadius="md">
          <Text fontSize="sm" color="gray.600">
            見積もりに含まれていない項目を管理します。ここで設定した項目は、各顧客の概算見積もりページの注釈として表示されます。
          </Text>
        </Box>

        {excludedItems.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color="gray.500">見積もり外項目が登録されていません</Text>
          </Box>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" bg="white" borderRadius="md" overflow="hidden" minW="400px">
            <Thead bg="gray.50">
              <Tr>
                <Th>名称</Th>
                <Th>備考</Th>
                <Th width="120px">操作</Th>
              </Tr>
            </Thead>
            <Tbody>
              {excludedItems.map((item) => (
                <Tr key={item.id}>
                  <Td fontWeight="bold">{item.name}</Td>
                  <Td>
                    {item.remarks ? (
                      <Text noOfLines={2}>{item.remarks}</Text>
                    ) : (
                      <Text color="gray.400">-</Text>
                    )}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        colorScheme="gray"
                        variant="outline"
                        onClick={() => handleOpenModal(item)}
                        aria-label="編集"
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleOpenDeleteDialog(item)}
                        aria-label="削除"
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      {/* 作成・編集モーダル */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditing ? '見積もり外項目編集' : '見積もり外項目新規作成'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>名称</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="見積もり外項目の名称を入力"
                />
              </FormControl>
              <FormControl>
                <FormLabel>備考</FormLabel>
                <Textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="備考を入力（任意）"
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              キャンセル
            </Button>
            <Button
              bg="black"
              color="white"
              _hover={{ bg: 'gray.700' }}
              onClick={handleSave}
              isLoading={isSubmitting}
              loadingText={isEditing ? '更新中...' : '作成中...'}
            >
              {isEditing ? '更新' : '作成'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              見積もり外項目の削除
            </AlertDialogHeader>
            <AlertDialogBody>
              「{itemToDelete?.name}」を削除しますか？
              <br />
              この操作は元に戻せません。
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                キャンセル
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                削除
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default ExcludedItemsManagement;
